'use client';

import { createContext, useContext, useMemo } from 'react';
import { useAccount } from 'wagmi';
import localConfig from 'mode-shared/configs/config';
import { useStakedModeTokens } from 'mode-shared/hooks/airdrop/lock/useStakedTokens';
import { useModeTokenBalance } from 'mode-shared/hooks/useModeTokenBalance';
import { useWhitelistedWallets } from 'mode-shared/hooks/content/hooks/useWhitelistedWallets';
import { useGetUserPromptsCount } from 'mode-shared/hooks/ai-terminal/useUserPromptsCount';

const MIN_MODE_AMOUNT = 400 * 1000;

const TerminalAccessContext = createContext<{
  promptsLeft: number;
  isReadOnly: boolean;
  isLoading: boolean;
  isFullAccess: boolean;
  isError: boolean;
}>({
  promptsLeft: localConfig.allowedPromptsCount,
  isReadOnly: false,
  isLoading: false,
  isFullAccess: false,
  isError: false,
});

export const usePromptsLeft = () => {
  const context = useContext(TerminalAccessContext);
  if (!context) {
    throw new Error('useTerminalContextAccess must be used within a TerminalAccessProvider');
  }
  return context;
};

export const TerminalAccessProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: promptsCount, isLoading: isLoadingPromptsCount, isError } = useGetUserPromptsCount();
  const { address } = useAccount();
  const { data: totalLockedModeAmount } = useStakedModeTokens();
  const { data: whitelistedWallets, isLoading: isLoadingWhitelistedWallets } = useWhitelistedWallets();
  const { data: modeTokenBalance } = useModeTokenBalance({
    token: localConfig.modeTokenAddress,
  });
  const isUserWhitelisted = useMemo(() => {
    return whitelistedWallets?.some((wallet) => wallet.toLowerCase() === address?.toLowerCase());
  }, [whitelistedWallets, address]);
  const isModeTokenStakedAmountEnough = totalLockedModeAmount && totalLockedModeAmount >= MIN_MODE_AMOUNT;
  const isModeTokenHoldingAmountEnough = modeTokenBalance?.value && Number(modeTokenBalance.value) >= MIN_MODE_AMOUNT;

  const isFullAccess = useMemo(() => {
    return !!isModeTokenStakedAmountEnough || !!isModeTokenHoldingAmountEnough || !!isUserWhitelisted;
  }, [isModeTokenStakedAmountEnough, isModeTokenHoldingAmountEnough, isUserWhitelisted]);

  const promptsLeft = useMemo(() => {
    if (isFullAccess) return localConfig.allowedPromptsCount;
    return localConfig.allowedPromptsCount - (promptsCount?.count ?? 0);
  }, [promptsCount, isFullAccess]);

  const isReadOnly = useMemo(() => {
    return !isFullAccess && promptsLeft <= 0;
  }, [isFullAccess, promptsLeft]);

  return (
    <TerminalAccessContext.Provider
      value={{
        promptsLeft,
        isReadOnly,
        isLoading: isLoadingPromptsCount || isLoadingWhitelistedWallets,
        isFullAccess,
        isError,
      }}
    >
      {children}
    </TerminalAccessContext.Provider>
  );
};
